// client/src/components/event/EventPublicSettings.jsx
//
// Publish control for an event's first-class public page (On-Screen 2.0).
// Route: /:tenant/:eventId/public-page  (linked from the Event Hub).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaHome, FaExternalLinkAlt } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import { updateEventPublicPage, PUBLIC_VISIBILITY, VISIBILITY_LABELS } from '../../services/publicEventApi';

const baseUrl = process.env.REACT_APP_BASE_URL || '';
const appOrigin = (process.env.REACT_APP_BASE_URL || window.location.origin || '').replace(/\/+$/, '');

export default function EventPublicSettings({ tenantId: tenantProp }) {
  const params = useParams();
  const tenantId = tenantProp || params.tenant;
  const { eventId } = params;
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [tenantSlug, setTenantSlug] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ev }, { data: t }] = await Promise.all([
        axios.get(`${baseUrl}/api/events/${tenantId}/events/${eventId}`),
        axios.get(`${baseUrl}/api/tenants/${tenantId}`).catch(() => ({ data: {} })),
      ]);
      const e = ev.event || {};
      setEvent(e);
      setTenantSlug(t?.tenant?.slug || t?.slug || null);
      setForm({
        visibility: e.public_visibility || 'private',
        slug: e.public_slug || '',
        headline: e.public_headline || '',
        ctaLabel: e.public_cta_label || '',
        ctaUrl: e.public_cta_url || '',
        venueName: e.venue_name || '',
        venueAddress: e.venue_address || '',
        venueLat: e.venue_lat ?? '',
        venueLng: e.venue_lng ?? '',
      });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [tenantId, eventId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError('');
    setSavedMsg('');
    try {
      const res = await updateEventPublicPage(tenantId, eventId, form);
      const p = res.public || {};
      setEvent((e) => ({ ...e, ...{
        public_visibility: p.public_visibility, public_slug: p.public_slug,
        public_headline: p.public_headline, public_cta_label: p.public_cta_label,
        public_cta_url: p.public_cta_url, venue_name: p.venue_name, venue_address: p.venue_address,
        venue_lat: p.venue_lat, venue_lng: p.venue_lng,
      } }));
      setForm((f) => ({ ...f, slug: p.public_slug || f.slug }));
      setSavedMsg(p.public_visibility === 'private' ? 'Public page turned off' : 'Public page saved');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <AppFooter buttons={[
      { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(`/${tenantId}/${eventId}/hub`) },
      { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenantId}/dashboard`) },
    ]} />
  );

  if (loading) return <div className="p-6 text-sm opacity-70" style={{ paddingBottom: 72 }}>Loading…{footer}</div>;
  if (!form) return <div className="p-6 text-sm text-red-600" style={{ paddingBottom: 72 }}>{error}{footer}</div>;

  const liveUrl = form.slug && tenantSlug ? `${appOrigin}/ev/${tenantSlug}/${form.slug}` : null;
  const isLive = (event?.public_visibility || 'private') !== 'private';

  const field = 'w-full rounded border border-black/15 bg-transparent px-2 py-1.5 text-sm dark:border-white/15';

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4" style={{ paddingBottom: 72 }}>
      <div>
        <h1 className="text-2xl font-semibold">Public event page</h1>
        <p className="text-sm opacity-60">{event?.name}</p>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {savedMsg && <div className="rounded bg-emerald-100 px-3 py-1.5 text-sm text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">{savedMsg}</div>}

      <section className="space-y-2 rounded-lg border border-black/10 p-4 dark:border-white/10">
        <label className="block text-xs font-semibold uppercase tracking-wide opacity-60">Visibility</label>
        {PUBLIC_VISIBILITY.map((v) => (
          <label key={v} className="flex items-center gap-2 text-sm">
            <input type="radio" name="vis" checked={form.visibility === v} onChange={() => set('visibility', v)} />
            {VISIBILITY_LABELS[v]}
          </label>
        ))}

        <label className="mt-2 block text-xs opacity-70">URL slug</label>
        <div className="flex items-center gap-1 text-sm">
          <span className="opacity-50">/ev/{tenantSlug || 'your-org'}/</span>
          <input className={field} value={form.slug} placeholder="auto from event name"
            onChange={(e) => set('slug', e.target.value)} />
        </div>
        {liveUrl && isLive && (
          <a href={liveUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-cobalt underline">
            <FaExternalLinkAlt size={10} /> {liveUrl}
          </a>
        )}
      </section>

      <section className="space-y-2 rounded-lg border border-black/10 p-4 dark:border-white/10">
        <div className="text-xs font-semibold uppercase tracking-wide opacity-60">Page content</div>
        <input className={field} placeholder="Hero headline (optional)" value={form.headline} onChange={(e) => set('headline', e.target.value)} />
        <div className="flex gap-2">
          <input className={field} placeholder="Register button label" value={form.ctaLabel} onChange={(e) => set('ctaLabel', e.target.value)} />
          <input className={field} placeholder="Register button URL" value={form.ctaUrl} onChange={(e) => set('ctaUrl', e.target.value)} />
        </div>
      </section>

      <section className="space-y-2 rounded-lg border border-black/10 p-4 dark:border-white/10">
        <div className="text-xs font-semibold uppercase tracking-wide opacity-60">Venue</div>
        <input className={field} placeholder="Venue name" value={form.venueName} onChange={(e) => set('venueName', e.target.value)} />
        <input className={field} placeholder="Address" value={form.venueAddress} onChange={(e) => set('venueAddress', e.target.value)} />
        <div className="flex gap-2">
          <input className={field} placeholder="Latitude (optional)" value={form.venueLat} onChange={(e) => set('venueLat', e.target.value)} />
          <input className={field} placeholder="Longitude (optional)" value={form.venueLng} onChange={(e) => set('venueLng', e.target.value)} />
        </div>
      </section>

      <div className="flex justify-end gap-2">
        <button onClick={save} disabled={saving} className="rounded bg-cobalt px-4 py-2 text-sm text-white disabled:opacity-50">
          {saving ? 'Saving…' : (form.visibility === 'private' ? 'Save (page off)' : 'Publish')}
        </button>
      </div>

      {footer}
    </div>
  );
}
